import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import { AdminPaymentsAPI } from '../../services/api';
import { formatRand, formatDateTime, apiErrorMessage } from '../charter/charterUtils';

const STATUS_LABELS = {
  'awaiting-merchant-feedback': 'Waiting for us',
  'awaiting-bank-feedback': 'Waiting for the bank',
  pending: 'Pending',
  resolved: 'Resolved',
  archived: 'Archived',
};

function bookingOf(d) {
  if (d.ride) return `Ride ${d.ride}`;
  if (d.trip) return `Trip ${d.trip}`;
  if (d.chargeKey && d.chargeKey.startsWith('ZS.')) return `Seat booking ${d.chargeKey.split('.')[1]}`;
  return '';
}

function outcomeOf(d) {
  if (d.status !== 'resolved') return '';
  return d.lost ? `Lost: ${formatRand(d.refundAmountCents || d.amountCents)} taken back` : 'Won';
}

/**
 * Paystack disputes (plan 5.9): open ones first, by due date. Responding
 * stays manual in the Paystack dashboard; the founders get an alert when a
 * dispute opens, at the first reminder for each status, and when it
 * resolves. A lost dispute opens a receivable for the operator's share.
 */
const DisputeList = () => {
  const [all, setAll] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminPaymentsAPI.disputes(all);
      setItems(res.data.data.items || []);
      setError('');
    } catch (e) {
      setItems([]);
      setError(apiErrorMessage(e, 'Could not load the disputes'));
    } finally {
      setLoading(false);
    }
  }, [all]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">Respond to each one in the Paystack dashboard before it is due.</p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Show resolved
        </label>
      </div>
      {error && <p className="mb-4 text-sm text-red-600" role="alert">{error}</p>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && <div className="p-6 text-sm text-gray-500">Loading...</div>}
        {!loading && items.length === 0 && <EmptyState title="No disputes" message={all ? 'No dispute has been filed.' : 'No dispute is open.'} icon={ShieldAlert} />}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Dispute', 'Status', 'Due', 'Amount', 'Charge', 'Booking', 'Outcome'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((d) => (
                  <tr key={d._id}>
                    <td className="px-4 py-3 font-mono text-gray-900">{d.gatewayDisputeId}{d.env === 'test' ? ' (test)' : ''}</td>
                    <td className="px-4 py-3 text-gray-700">{STATUS_LABELS[d.status] || d.status}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDateTime(d.dueAt)}</td>
                    <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{formatRand(d.amountCents)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{d.chargeKey || d.transactionReference}</td>
                    <td className="px-4 py-3 text-gray-700">{bookingOf(d)}</td>
                    <td className="px-4 py-3 text-gray-700">{outcomeOf(d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeList;
